import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './styles/globals.css';
import App from './App';
import sentryConfig from './config/sentry';

/**
 * Sentry is initialised before anything renders, so a crash during the first
 * paint is still reported. Until this existed, a render-phase crash in the admin
 * app white-screened it and left no record anywhere.
 *
 * A build with no DSN skips initialisation rather than calling init() with an
 * empty string, which warns on every page load.
 */
if (sentryConfig.enabled) {
  Sentry.init({
    ...sentryConfig,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        /**
         * Same posture as the customer app (plan §3.3): what a user TYPED is
         * masked, what was rendered to them is not. This app displays customer
         * contact details, vehicle registrations and damage photographs, so the
         * same privacy-policy consequence applies.
         *
         * To exclude an element use Sentry's default selectors rather than
         * changing these flags:
         *   class="sentry-block"  — not recorded at all
         *   class="sentry-mask"   — recorded, text masked
         */
        maskAllText: false,
        maskAllInputs: true,
        blockAllMedia: false,
      }),
    ],
  });
}

/**
 * A deliberate way to prove reporting works, from the devtools console:
 *
 *   __sentryTest()
 *
 * Defined in the bundle ON PURPOSE. The Error is constructed here, so its stack
 * carries a real http(s) frame and survives the `beforeSend` filter in
 * config/sentry.js. An error thrown straight from the console does NOT — every
 * frame is `debugger eval code` or `<anonymous>` — so it is dropped silently and
 * looks exactly like Sentry being broken when it is working perfectly.
 *
 * Absent from production builds: it would otherwise be a way past that same
 * noise filter, and the free tier's error budget is small enough to care.
 */
if (sentryConfig.environment !== 'production') {
  window.__sentryTest = (message) => {
    const error = new Error(message || 'PanelGear Sentry test — raised deliberately');
    Sentry.captureException(error);
    return `sent to Sentry (${sentryConfig.environment}) — look for: ${error.message}`;
  };
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App />);
